import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { AuthorText } from '../../components/AuthorText';
import { Credits } from '../../components/Credits';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { S, faq, t, type Leaf } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { usePrefersReducedMotion } from '../../lib/motion';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines } from '../../state/derived';
import styles from './matrix.module.css';
import { MatrixShell } from './MatrixShell';
import { makeRain } from './rain';

/** Cadence de la machine à écrire du journal de démarrage. */
const TYPE_MS = 25;

/**
 * Les trois lignes de démarrage, tapées caractère par caractère.
 *
 * Sous « mouvement réduit », tout est déjà écrit : le texte reste, seule
 * l'animation disparaît.
 */
function useBootLog(lines: readonly string[], instant: boolean): readonly string[] {
  const total = lines.reduce((sum, line) => sum + line.length, 0);
  const [typed, setTyped] = useState(instant ? total : 0);

  useEffect(() => {
    if (instant) {
      setTyped(total);
      return;
    }
    setTyped(0);
    const id = window.setInterval(() => {
      setTyped((previous) => {
        if (previous >= total) {
          window.clearInterval(id);
          return previous;
        }
        return previous + 1;
      });
    }, TYPE_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [instant, total]);

  let remaining = typed;
  return lines.map((line) => {
    const shown = line.slice(0, Math.max(0, remaining));
    remaining -= line.length;
    return shown;
  });
}

/** Skin 6 — Matrix : le site comme une session shell, vert sur noir. */
export function MatrixSkin(): ReactNode {
  const { lang, seen, playing, clock } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();
  const questions = faq(lang);
  const reducedMotion = usePrefersReducedMotion();

  // Tirée une fois par visite : la pluie ne se recalcule jamais au rendu.
  const rain = useMemo(() => makeRain(), []);

  const bootLines: readonly Leaf[] = [S.matrix.shell.boot_1, S.matrix.shell.boot_2, S.matrix.shell.boot_3];
  const typed = useBootLog(
    bootLines.map((line) => t(line, lang)),
    reducedMotion,
  );

  return (
    <div className={cx(styles.root, reducedMotion && styles.still)}>
      {/* Trois calques décoratifs, aucun n'intercepte le moindre clic. */}
      <div className={cx(styles.rain, playing && styles.rainFast)} aria-hidden="true">
        {rain.map((column) => (
          <span
            key={`${String(column.left)}-${column.glyphs}`}
            className={styles.rainColumn}
            style={
              {
                left: `${column.left.toFixed(1)}%`,
                '--rain-duration': `${column.duration.toFixed(1)}s`,
                animationDelay: `${column.delay.toFixed(1)}s`,
              } as CSSProperties
            }
          >
            {column.glyphs}
          </span>
        ))}
      </div>
      <div className={styles.vignette} aria-hidden="true" />
      <div className={styles.scanlines} aria-hidden="true" />

      <div className={styles.column}>
        <header className={styles.session}>
          <span className={styles.dim}>{t(S.matrix.shell.prompt, lang)}</span>
          <span className={styles.dim} role="status">
            {t(S.matrix.shell.status, lang, {
              n: seen,
              clock,
              state: t(playing ? S.matrix.shell.state_signal : S.matrix.shell.state_silence, lang),
            })}
          </span>
        </header>

        <div className={styles.boot}>
          {typed.map((line, index) => (
            <div key={bootLines[index]?.fr ?? index} className={styles.dim}>
              {line}
              {index === typed.length - 1 && <span className={styles.caret} aria-hidden="true" />}
            </div>
          ))}
        </div>

        <section className={styles.hero}>
          <h1 className={styles.title}>{t(S.matrix.hero.title, lang)}</h1>
          {/*
            Steve est le seul objet non vert de l'écran : le halo est vert, le
            poisson reste orange. C'est la blague du skin, rien ne doit la
            diluer.
          */}
          <div className={styles.steveHolder}>
            <Steve bobDuration="3.2s" onClick={actions.bump} label={t(S.common.title, lang)} />
          </div>
          <p className={styles.dim}>{t(S.matrix.hero.caption, lang)}</p>
        </section>

        <section className={styles.panel}>
          <p className={styles.command}>{t(S.matrix.commands.lore, lang)}</p>
          <p className={styles.paragraph}>{t(S.y2k.who_p1, lang)}</p>
          <p className={styles.paragraph}>
            <AuthorText linkClassName={styles.creditLink}>{t(S.matrix.lore.p2, lang)}</AuthorText>
          </p>
        </section>

        <div className={styles.counters}>
          <section className={styles.panel}>
            <span className={styles.counterValue}>{seen}</span>
            <span className={styles.dim}>{t(S.matrix.stats.seen, lang)}</span>
          </section>
          <section className={styles.panel}>
            <span className={styles.counterValue}>2</span>
            <span className={styles.dim}>{t(S.matrix.stats.legs, lang)}</span>
          </section>
          <section className={styles.panel}>
            <span className={styles.counterValue}>100 %</span>
            <span className={styles.dim}>{t(S.matrix.stats.orange, lang)}</span>
          </section>
        </div>

        <section className={styles.panel}>
          <p className={styles.command}>{t(S.matrix.commands.mixer, lang)}</p>

          <VuMeter className={styles.vu} barClassName={styles.vuBar} />

          <div className={styles.pads}>
            {mixer.pads.map((pad) => (
              <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                <span>[{pad.label}]</span>
                <span className={styles.dim}>{pad.timeLabel}</span>
              </button>
            ))}
          </div>

          <div className={styles.buttons}>
            <button type="button" className={styles.button} onClick={actions.toggleAudio}>
              {audio.label}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
              {mixer.holdLabel}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onStop}>
              {t(S.matrix.mixer.stop, lang)}
            </button>
          </div>

          {mixer.sliders.map((slider) => (
            <div key={slider.key} className={styles.slider}>
              <label className={cx(styles.sliderLabel, styles.dim)} htmlFor={`matrix-${slider.key}`}>
                {slider.label}
              </label>
              <input
                id={`matrix-${slider.key}`}
                type="range"
                className={styles.sliderInput}
                min={slider.min}
                max={slider.max}
                step={slider.step}
                value={slider.value}
                onChange={(event) => {
                  slider.onChange(event.currentTarget.valueAsNumber);
                }}
              />
              <output className={styles.sliderValue} htmlFor={`matrix-${slider.key}`}>
                {slider.valueLabel}
              </output>
            </div>
          ))}

          <div className={styles.buttons}>
            {mixer.presets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                className={styles.button}
                onClick={preset.onSelect}
              >
                {preset.label}
              </button>
            ))}
            <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
              {mixer.wobbleLabel}
            </button>
          </div>

          <p className={styles.dim} role="status">
            {audio.hint}
          </p>
        </section>

        <section className={styles.panel}>
          <p className={styles.command}>{t(S.matrix.commands.faq, lang)}</p>
          <dl className={styles.faq}>
            {questions.map((item) => (
              <div key={item.question}>
                <dt className={styles.question}>{item.question}</dt>
                <dd className={cx(styles.answer, styles.dim)}>{item.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={styles.panel}>
          <p className={styles.command}>{t(S.matrix.commands.guestbook, lang)}</p>
          <ul className={styles.entries}>
            {guestbook.map((entry) => (
              <li key={entry.id} className={styles.entry}>
                <span className={styles.dim}>[{entry.who}]</span> {entry.text}
              </li>
            ))}
          </ul>
          {/*
            L'invite du livre d'or est aussi un shell : une ligne qui commence
            par une commande connue est exécutée, tout le reste est publié.
          */}
          <MatrixShell />
        </section>

        <footer className={cx(styles.credits, styles.dim)}>
          <Credits linkClassName={styles.creditLink} unofficial />
          <span>{t(S.matrix.egg_hint, lang)}</span>
        </footer>
      </div>
    </div>
  );
}
